import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import companiesService, { Company } from "../../features/companies/companiesService";
import { isAdmin } from "../../utils/permissions";
import { setSelectedClient } from "../../features/settings/settingsSlice";

const dummyReports = [
  {
    id: 1,
    title: "Competitor Analysis: Industry Trends",
    summary: "Market growth +8%; video content conversion on social channels +23%.",
    tags: ["competitor", "trend", "social"],
  },
  {
    id: 2,
    title: "Market Research: Audience Insights",
    summary: "Users 25-34 are most active on weekdays between 8–10 PM.",
    tags: ["persona", "timing", "behavior"],
  },
  {
    id: 3,
    title: "Content Strategy: SEO Opportunities",
    summary: "Long-tail keywords present a +35% potential uplift in blog traffic.",
    tags: ["seo", "blog", "content"],
  },
];

const DeepResearchPage: React.FC = () => {
  const [q, setQ] = useState("");
  const dispatch = useDispatch();
  const { user } = useSelector((s: RootState) => s.auth);
  const [singleCompanies, setSingleCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | "">("");
  useEffect(() => {
    const load = async () => {
      if (!isAdmin(user)) return;
      const { companies } = await companiesService.getCompanies({ company_type: 'single', limit: 200 });
      setSingleCompanies(companies || []);
    };
    load();
  }, [user]);
  const { t } = useTranslation();
  const filtered = dummyReports.filter((r) =>
    (r.title + " " + r.summary + " " + r.tags.join(" ")).toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="deep-research-page">
      <div className="page-header">
        <div className="header-left">
          <div className="header-icon">
            <i className="bi bi-search" />
          </div>
          <div className="header-text">
            <h1>Deep Research</h1>
            <p>Explore insights, competitor signals, and market trends</p>
          </div>
        </div>
        <div className="header-right">
          {isAdmin(user) && (
            <div>
              <select
                className="form-select"
                value={selectedCompanyId}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : "";
                  setSelectedCompanyId(val);
                  (dispatch as any)(setSelectedClient(typeof val === 'number' ? val : undefined));
                }}
                style={{ minWidth: 220 }}
              >
                <option value="">Select company</option>
                {singleCompanies.map((c) => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="search-box">
            <i className="bi bi-search" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search research items..."
            />
          </div>
        </div>
      </div>

      <div className="reports-grid">
        {filtered.map((r) => (
          <div key={r.id} className="report-card">
            <div className="report-header">
              <div className="report-icon"><i className="bi bi-graph-up" /></div>
              <div className="report-meta">
                <div className="report-title">{r.title}</div>
                <div className="report-sub">Generated 2 hours ago • Confidence: High</div>
              </div>
            </div>
            <div className="report-summary">{r.summary}</div>
            <div className="report-tags">
              {r.tags.map((t) => (
                <span key={t} className="tag">{t}</span>
              ))}
            </div>
            <div className="report-actions">
              <button className="btn btn-sm btn-outline-primary">Open Brief</button>
              <button className="btn btn-sm btn-outline-secondary">Export</button>
            </div>
          </div>
        ))}
      </div>

      <style>{`
.deep-research-page { padding: 24px; }
.page-header { display:flex; justify-content:space-between; gap:16px; padding:20px 24px; background:var(--bg-primary); border:1px solid var(--border-color); border-radius:16px; }
.header-left { display:flex; align-items:center; gap:14px; }
.header-icon { width:46px; height:46px; border-radius:12px; background: linear-gradient(135deg, #10b981, #14b8a6); color:#fff; display:flex; align-items:center; justify-content:center; font-size:20px; }
.header-text h1 { margin:0; font-size:22px; font-weight:700; color:var(--text-primary); }
.header-text p { margin:2px 0 0; color:var(--text-muted); font-size:14px; font-weight:500; }
.search-box { display:flex; align-items:center; gap:8px; background: var(--bg-tertiary); border:1px solid var(--border-color); border-radius:12px; padding:10px 12px; }
.search-box input { border:none; outline:none; background:transparent; width:100%; color:var(--text-primary); font-size:14px; }
.reports-grid { margin-top:20px; display:grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap:16px; }
.report-card { background: var(--bg-primary); border:1px solid rgba(148,163,184,.6); border-radius:16px; padding:16px; transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease; }
.report-card:hover { transform: translateY(-3px); box-shadow: 0 14px 30px rgba(15,23,42,.15); border-color: rgba(59,130,246,.5); }
.report-header { display:flex; align-items:center; gap:12px; margin-bottom:10px; }
.report-icon { width:36px; height:36px; border-radius:10px; background: linear-gradient(135deg, #6366f1, #3b82f6); color:#fff; display:flex; align-items:center; justify-content:center; }
.report-meta { display:flex; flex-direction:column; gap:2px; }
.report-title { font-weight:800; color:var(--text-primary); font-size:16px; }
.report-sub { font-size:12px; color: var(--text-muted); }
.report-summary { color:var(--text-secondary); font-size:13px; margin:8px 0; line-height:1.6; }
.report-tags { margin-top:10px; display:flex; flex-wrap:wrap; gap:6px; }
.tag { background: var(--bg-tertiary); color: var(--text-secondary); border:1px solid var(--border-color); border-radius:999px; font-size:12px; padding:4px 10px; }
.report-actions { display:flex; gap:8px; margin-top:12px; }
      `}</style>
    </div>
  );
};

export default DeepResearchPage;


